import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { ConnectionCardProps } from "./ConnectionCard";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  connectionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#fff7ed",
    borderRadius: 8,
  },
  connectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#ea580c",
    marginBottom: 12,
  },
  twoColumnLayout: {
    flexDirection: "row",
    gap: 12,
  },
  column: {
    flex: 1,
  },
  contextTitle: {
    color: "#2563eb",
  },
  applicationTitle: {
    color: "#059669",
  },
  professionsTitle: {
    color: "#9333ea",
  },
  professionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  professionTag: {
    backgroundColor: "#f3f4f6",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    color: "#374151",
  },
});

const ConnectionDocument = ({
  courseName,
  prompt,
  gradeLevel,
  connections,
  imageUrl,
  customizationSettings,
  author,
  createdAt,
}: ConnectionCardProps) => {
  const { t } = useTranslation();
  const sectionMarkdownStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };
  return (
    <PdfLayout
      courseName={courseName}
      gradeLevel={gradeLevel}
      topic={prompt}
      imageUrl={imageUrl}
      headerTextColor="#ea580c"
      customizationSettings={customizationSettings}
      author={author}
      createdDate={createdAt}
    >
      {connections?.map((connection, index) => (
        <View wrap={false} key={index} style={styles.connectionContainer}>
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.connectionTitle,
                  fontSize: scaleFontSize(
                    styles.connectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              },
            }}
            content={connection.title}
          />

          <View style={styles.twoColumnLayout}>
            <View style={styles.column}>
              {/* Context Description */}
              <View>
                <Text
                  style={[
                    commonStyles.sectionTitle,
                    styles.contextTitle,
                    {
                      fontSize: scaleFontSize(
                        commonStyles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("connections.context")}
                </Text>
                <MarkdownRenderer
                  customStyle={sectionMarkdownStyle}
                  content={connection.context_description}
                />
              </View>

              {/* Real World Application */}
              {connection.real_world_application && (
                <View style={{ marginTop: 12 }}>
                  <Text
                    style={[
                      commonStyles.sectionTitle,
                      styles.applicationTitle,
                      {
                        fontSize: scaleFontSize(
                          commonStyles.sectionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("connections.connectionsApplication")}
                  </Text>
                  <MarkdownRenderer
                    content={connection.real_world_application}
                    customStyle={sectionMarkdownStyle}
                  />
                </View>
              )}
            </View>

            {/* Professions */}
            <View style={styles.column}>
              <Text
                style={[
                  commonStyles.sectionTitle,
                  styles.professionsTitle,
                  {
                    fontSize: scaleFontSize(
                      commonStyles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("connections.professions")}
              </Text>
              <View style={styles.professionsContainer}>
                {connection.professions.map((profession, idx) => (
                  <MarkdownRenderer
                    key={idx}
                    customStyle={{
                      text: {
                        text: {
                          ...styles.professionTag,
                          fontSize: scaleFontSize(
                            styles.professionTag.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          ...styles.professionTag,
                        },
                      },
                    }}
                    content={profession}
                  />
                ))}
              </View>
            </View>
          </View>
        </View>
      ))}
    </PdfLayout>
  );
};

function ConnectionCardPDF(props: ConnectionCardProps) {
  return <ConnectionDocument {...props} />;
}

export default ConnectionCardPDF;
